import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface BreadcrumbItem {
  title: ReactNode;
  /** Path to navigate to when clicked. Omit on the last (current-page) crumb. */
  path?: string;
  /** Optional icon shown before the title — typically the top-level page icon. */
  icon?: ReactNode;
}

interface LayoutContextType {
  heading: string | ReactNode;
  setHeading: (heading: string | ReactNode) => void;
  headLoading: boolean;
  setHeadLoading: (loading: boolean) => void;
  extraContent: React.ReactNode;
  setExtraContent: (content: React.ReactNode) => void;
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (crumbs: BreadcrumbItem[]) => void;
  initialRoutesConfig: any;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const LayoutProvider: React.FC<{
  children: ReactNode;
  initialRoutesConfig: any;
}> = ({ children, initialRoutesConfig }) => {
  const [heading, setHeading] = useState<React.ReactNode>('');
  const [headLoading, setHeadLoading] = useState<boolean>(false);
  const [extraContent, setExtraContent] = useState<React.ReactNode>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  return (
    <LayoutContext.Provider
      value={{
        heading,
        setHeading,
        headLoading,
        setHeadLoading,
        extraContent,
        setExtraContent,
        breadcrumbs,
        setBreadcrumbs,
        initialRoutesConfig,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
